/**
 * Copying text out of the console, and knowing whether it happened.
 *
 * One implementation, because there are two callers with the same requirement
 * and they must not disagree: `NudgeSheet` (a drafted email) and the item
 * record's field rows (`ItemDetail`, where the row itself is the copy
 * target). Both exist so a person can take words somewhere else — Procore, an
 * email client — so a copy that silently did nothing is the one failure that
 * matters, and both surface it.
 *
 * There used to be a `CopyButton` component between the rows and this
 * function. It was deleted when the row absorbed the affordance: a button
 * with exactly one caller, holding a second copy of the confirm-then-reset
 * state this file's callers each need anyway, is a layer rather than a
 * component.
 *
 * `navigator.clipboard` is missing in insecure contexts and in some embedded
 * webviews, and `writeText` rejects outright when the document is not focused
 * (which is exactly what happens if a click lands while a devtools pane or
 * another window has focus). Every one of those returns `false` here rather
 * than throwing into a click handler.
 */

export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    const clipboard = navigator?.clipboard;
    if (!clipboard?.writeText) return false;
    await clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
